'use client';

import React from 'react';
import { Wifi, Coffee, Monitor, Users, CalendarDays, Lock, CheckCircle2, ArrowRight, MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import Icon from '@/components/ui/AppIcon';


export default function CoworkingSection() {
  const { t } = useLanguage();

  const AMENITIES = [
    { id: 'am-1', icon: Wifi, labelKey: 'cw.amenity1', descKey: 'cw.amenity1.desc' },
    { id: 'am-2', icon: Coffee, labelKey: 'cw.amenity2', descKey: 'cw.amenity2.desc' },
    { id: 'am-3', icon: Monitor, labelKey: 'cw.amenity3', descKey: 'cw.amenity3.desc' },
    { id: 'am-4', icon: Users, labelKey: 'cw.amenity4', descKey: 'cw.amenity4.desc' },
    { id: 'am-5', icon: CalendarDays, labelKey: 'cw.amenity5', descKey: 'cw.amenity5.desc' },
    { id: 'am-6', icon: Lock, labelKey: 'cw.amenity6', descKey: 'cw.amenity6.desc' },
  ];

  const PLANS = [
    {
      id: 'plan-hot',
      nameKey: 'cw.hotDesk',
      price: '€89',
      periodKey: 'cw.month',
      descKey: 'cw.plan.hotDesk.desc',
      featureKeys: ['cw.plan.hotDesk.f1', 'cw.plan.hotDesk.f2', 'cw.plan.hotDesk.f3', 'cw.plan.hotDesk.f4'],
    },
    {
      id: 'plan-dedicated',
      nameKey: 'cw.dedicatedDesk',
      price: '€149',
      periodKey: 'cw.month',
      descKey: 'cw.plan.dedicated.desc',
      featureKeys: ['cw.plan.dedicated.f1', 'cw.plan.dedicated.f2', 'cw.plan.dedicated.f3', 'cw.plan.dedicated.f4'],
      featured: true,
    },
    {
      id: 'plan-private',
      nameKey: 'cw.privateOffice',
      price: '€320',
      periodKey: 'cw.month',
      descKey: 'cw.plan.private.desc',
      featureKeys: ['cw.plan.private.f1', 'cw.plan.private.f2', 'cw.plan.private.f3', 'cw.plan.private.f4'],
    },
  ];

  return (
    <section id="coworking" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-14">
          <div>
            <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('cw.tag')}</p>
            <h2 className="font-display text-4xl font-bold text-[#1B3A5C] tracking-tight leading-tight">
              {t('cw.sectionTitle')?.split('\n')?.map((line, i) => (
                <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
              ))}
            </h2>
          </div>
          <div>
            <p className="text-slate-500 text-base leading-relaxed mb-4">
              {t('cw.sectionDesc')}
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <MapPin size={15} className="text-[#C9A96E]" />
              Topčiderska ulica, Beograd
            </div>
          </div>
        </div>

        {/* Amenities */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-14">
          {AMENITIES?.map((a) => {
            const Icon = a?.icon;
            return (
              <div key={a?.id} className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border border-slate-200 text-center hover:border-[#1B3A5C]/30 hover:shadow-md transition-all duration-150 group">
                <div className="w-10 h-10 rounded-xl bg-[#1B3A5C]/6 flex items-center justify-center group-hover:bg-[#1B3A5C]/10 transition-colors">
                  <Icon size={18} className="text-[#1B3A5C]" />
                </div>
                <span className="text-xs font-semibold text-slate-700">{t(a?.labelKey)}</span>
                <span className="text-[10px] text-slate-400">{t(a?.descKey)}</span>
              </div>
            );
          })}
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS?.map((plan) => (
            <div
              key={plan?.id}
              className={`rounded-2xl border p-7 flex flex-col transition-all duration-200 ${
                plan?.featured
                  ? 'bg-[#1B3A5C] border-[#1B3A5C] text-white shadow-xl scale-[1.02]'
                  : 'bg-white border-slate-200 hover:border-[#1B3A5C]/25 hover:shadow-lg'
              }`}
            >
              {plan?.featured && (
                <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-[#C9A96E] mb-3">{t('cw.getStarted') ? t('vo.popular') : 'Most Popular'}</span>
              )}
              <h3 className={`font-display font-bold text-xl mb-1 ${plan?.featured ? 'text-white' : 'text-[#1B3A5C]'}`}>
                {t(plan?.nameKey)}
              </h3>
              <div className="flex items-end gap-1 my-3">
                <span className={`font-display font-bold text-3xl ${plan?.featured ? 'text-white' : 'text-[#1B3A5C]'}`}>
                  {plan?.price}
                </span>
                <span className={`text-sm mb-1 ${plan?.featured ? 'text-slate-300' : 'text-slate-400'}`}>{t(plan?.periodKey)}</span>
              </div>
              <p className={`text-sm leading-relaxed mb-5 ${plan?.featured ? 'text-slate-300' : 'text-slate-500'}`}>
                {t(plan?.descKey)}
              </p>
              <ul className="space-y-2 mb-6 flex-1">
                {plan?.featureKeys?.map((fk) => (
                  <li key={fk} className={`flex items-start gap-2 text-xs ${plan?.featured ? 'text-slate-300' : 'text-slate-600'}`}>
                    <CheckCircle2 size={13} className={`shrink-0 mt-0.5 ${plan?.featured ? 'text-[#C9A96E]' : 'text-emerald-500'}`} />
                    {t(fk)}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 ${
                  plan?.featured
                    ? 'bg-[#C9A96E] text-white hover:bg-[#b8944f]'
                    : 'border border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C]/5'
                }`}
              >
                {t('cw.getStarted')} <ArrowRight size={14} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}